/* Auto top-up settings dialog — user picks a threshold and a recharge
   amount; when the wallet drops below the threshold the backend sends a
   one-tap checkout link by push + email. */
import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, RefreshCw } from "lucide-react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Switch } from "../ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../ui/dialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AutoTopupDialog({ open, onClose, onSaved }) {
  const [enabled, setEnabled] = useState(false);
  const [threshold, setThreshold] = useState(200);
  const [amount, setAmount] = useState(1000);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    (async () => {
      try {
        const r = await axios.get(`${API}/doorbell/wallet/auto-topup`, { withCredentials: true });
        setEnabled(!!r.data.enabled);
        setThreshold(r.data.threshold_ngn);
        setAmount(r.data.amount_ngn);
      } catch { /* keep defaults */ }
    })();
  }, [open]);

  const save = async () => {
    setBusy(true);
    try {
      const r = await axios.post(
        `${API}/doorbell/wallet/auto-topup`,
        { enabled, threshold_ngn: Number(threshold) || 0, amount_ngn: Number(amount) || 0 },
        { withCredentials: true },
      );
      toast.success(r.data.enabled
        ? `Auto top-up on — below ₦${r.data.threshold_ngn.toLocaleString()} we'll send a ₦${r.data.amount_ngn.toLocaleString()} top-up link`
        : "Auto top-up turned off");
      onSaved?.(r.data);
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not save auto top-up settings");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="rounded-sm sm:max-w-md" data-testid="auto-topup-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading tracking-tight flex items-center gap-2">
            <RefreshCw className="w-4 h-4 text-emerald-600" /> Auto top-up
          </DialogTitle>
          <DialogDescription className="font-body text-xs">
            When your wallet drops below the threshold, we instantly send you a
            one-tap payment link (push + email) for your chosen amount — so
            calls and Safe Route advisories never stall.
          </DialogDescription>
        </DialogHeader>

        <div className="flex items-center justify-between py-1">
          <Label className="text-sm font-body">Enable auto top-up</Label>
          <Switch checked={enabled} onCheckedChange={setEnabled} data-testid="auto-topup-switch" />
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label className="text-[10px] uppercase tracking-widest font-bold text-gray-500 mb-1 block">When below (₦)</Label>
            <Input
              type="number"
              min={50}
              value={threshold}
              disabled={!enabled}
              onChange={(e) => setThreshold(e.target.value)}
              className="rounded-sm h-10 text-sm"
              data-testid="auto-topup-threshold-input"
            />
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-widest font-bold text-gray-500 mb-1 block">Top up by (₦)</Label>
            <Input
              type="number"
              min={100}
              step={100}
              value={amount}
              disabled={!enabled}
              onChange={(e) => setAmount(e.target.value)}
              className="rounded-sm h-10 text-sm"
              data-testid="auto-topup-amount-input"
            />
          </div>
        </div>
        <p className="text-[10px] text-gray-400 font-body -mt-1">
          No card is stored — you confirm each payment with one tap. Max one
          prompt every 6 hours.
        </p>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} className="rounded-sm font-body text-xs" data-testid="auto-topup-cancel">
            Cancel
          </Button>
          <Button onClick={save} disabled={busy} className="bg-black hover:bg-gray-800 text-white rounded-sm font-body text-xs" data-testid="auto-topup-save">
            {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : "Save"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
